import { api, unwrap } from "@lumberset/api-client";
import { money } from "@lumberset/takeoff-ui";
import {
  Badge,
  Button,
  Container,
  Drawer,
  EmptyState,
  Field,
  Input,
  Prompt,
  SectionHeader,
  Switch,
  Table,
  toast,
} from "@lumberset/ui";
import { Gift, Plus } from "@medusajs/icons";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { Crumbs } from "../../components/crumbs";
import { centsToDollars, dollarsToCents, wholeNumber } from "../../lib/money";
import { billingPlansQuery, creditPacksQuery } from "../../lib/queries";

export const Route = createFileRoute("/_app/credit-packs")({
  loader: ({ context: { queryClient } }) => queryClient.ensureQueryData(creditPacksQuery),
  component: CreditPacks,
});

type Pack = Awaited<ReturnType<NonNullable<typeof creditPacksQuery.queryFn>>>["packs"][number];

const SLUG_PATTERN = "[a-z0-9-]{2,40}";

function CreditPacks() {
  const { data } = useSuspenseQuery(creditPacksQuery);
  const { data: billingPlans } = useQuery(billingPlansQuery);
  const [editing, setEditing] = useState<Pack | "new" | null>(null);

  // Savings are shown against the default Billing Plan's Quote Fee.
  const quoteFee =
    billingPlans?.billingPlans.find((billingPlan) => billingPlan.isDefault)?.quoteFeeCents ??
    billingPlans?.billingPlans[0]?.quoteFeeCents;

  return (
    <>
      <Crumbs trail={[{ label: "Credit packs" }]} />
      <Container className="divide-y p-0">
        <SectionHeader
          level="h1"
          title="Credit packs"
          description="Prepaid takeoffs sold at a discount. A credit covers one released takeoff, after the trial and before the Quote Fee."
          actions={
            <Button size="small" onClick={() => setEditing("new")}>
              <Plus /> New credit pack
            </Button>
          }
        />

        {data.packs.length === 0 ? (
          <EmptyState
            icon={<Gift />}
            title="No credit packs yet"
            description="Create one and it appears in every workspace's billing settings."
          />
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Credit pack</Table.HeaderCell>
                <Table.HeaderCell className="text-right">Credits</Table.HeaderCell>
                <Table.HeaderCell className="text-right">Price</Table.HeaderCell>
                <Table.HeaderCell className="text-right">Per takeoff</Table.HeaderCell>
                <Table.HeaderCell className="text-right">Saving</Table.HeaderCell>
                <Table.HeaderCell className="text-right">Position</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data.packs.map((pack) => {
                const each = Math.round(pack.priceCents / pack.quotes);
                const saving =
                  quoteFee !== undefined ? quoteFee * pack.quotes - pack.priceCents : null;
                return (
                  <Table.Row key={pack.id} data-clickable="true" onClick={() => setEditing(pack)}>
                    <Table.Cell>
                      <div className="flex items-center gap-x-2">
                        <span className="txt-compact-small-plus text-ui-fg-base">{pack.name}</span>
                        {pack.active ? null : <Badge color="orange">Inactive</Badge>}
                      </div>
                      <div className="txt-compact-xsmall font-mono text-ui-fg-muted">
                        {pack.slug}
                      </div>
                    </Table.Cell>
                    <Table.Cell className="tabular text-right">{pack.quotes}</Table.Cell>
                    <Table.Cell className="tabular whitespace-nowrap text-right text-ui-fg-base">
                      {money(pack.priceCents)}
                    </Table.Cell>
                    <Table.Cell className="tabular whitespace-nowrap text-right">
                      {money(each)}
                    </Table.Cell>
                    <Table.Cell className="tabular whitespace-nowrap text-right">
                      {saving === null
                        ? "-"
                        : saving > 0 && quoteFee
                          ? `${money(saving)} (${Math.round((saving / (quoteFee * pack.quotes)) * 100)}%)`
                          : "None"}
                    </Table.Cell>
                    <Table.Cell className="tabular text-right">{pack.position}</Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        )}
      </Container>

      <Drawer open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        <Drawer.Content>
          {editing !== null ? (
            <PackForm
              key={editing === "new" ? "new" : editing.id}
              existing={editing === "new" ? null : editing}
              onClose={() => setEditing(null)}
            />
          ) : null}
        </Drawer.Content>
      </Drawer>
    </>
  );
}

/** Creates or edits one credit pack. Keyed by its id so state starts fresh each time. */
function PackForm({ existing, onClose }: { existing: Pack | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [active, setActive] = useState(existing?.active ?? true);
  const [deleting, setDeleting] = useState(false);

  const done = async (message: string) => {
    await queryClient.invalidateQueries({ queryKey: ["admin", "credit-packs"] });
    toast.success(message);
    onClose();
  };

  const save = useMutation({
    mutationFn: (form: FormData) => {
      const text = (name: string) => String(form.get(name) ?? "").trim();
      const json = {
        slug: text("slug"),
        name: text("name"),
        quotes: Math.max(1, wholeNumber(form.get("quotes"), 1)),
        priceCents: dollarsToCents(form.get("price")) ?? 0,
        active,
        position: wholeNumber(form.get("position")),
      };
      return existing
        ? unwrap(api.admin["credit-packs"][":id"].$patch({ param: { id: existing.id }, json }))
        : unwrap(api.admin["credit-packs"].$post({ json }));
    },
    onSuccess: ({ pack }) => done(existing ? `${pack.name} saved` : `${pack.name} created`),
    onError: (error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: () =>
      unwrap(api.admin["credit-packs"][":id"].$delete({ param: { id: existing!.id } })),
    onSuccess: () => done(`${existing!.name} deleted`),
    onError: (error) => toast.error(error.message),
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    save.mutate(new FormData(event.currentTarget));
  }

  return (
    <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
      <Drawer.Header>
        <Drawer.Title>{existing ? existing.name : "New credit pack"}</Drawer.Title>
        <Drawer.Description>
          Money is in dollars here and stored as cents. Purchases already made keep their price.
        </Drawer.Description>
      </Drawer.Header>

      <Drawer.Body className="flex flex-col gap-y-5">
        <Field label="Name" hint="As Members see it, for example: 10 takeoffs.">
          {(control) => (
            <Input
              {...control}
              name="name"
              required
              maxLength={80}
              autoFocus
              defaultValue={existing?.name ?? ""}
            />
          )}
        </Field>

        <Field
          label="Slug"
          hint="Lowercase letters, digits and hyphens. It names the pack in the seed."
        >
          {(control) => (
            <Input
              {...control}
              name="slug"
              required
              pattern={SLUG_PATTERN}
              title="Lowercase letters, digits and hyphens, 2 to 40 characters."
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder="10-takeoffs"
              defaultValue={existing?.slug ?? ""}
            />
          )}
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Credits" hint="Takeoffs the pack pays for.">
            {(control) => (
              <Input
                {...control}
                name="quotes"
                type="number"
                inputMode="numeric"
                min={1}
                max={10000}
                step={1}
                required
                defaultValue={existing?.quotes ?? 5}
              />
            )}
          </Field>
          <Field label="Price">
            {(control) => (
              <Input
                {...control}
                name="price"
                inputMode="decimal"
                prefix="$"
                required
                pattern="[0-9]*\.?[0-9]{0,2}"
                defaultValue={centsToDollars(existing?.priceCents ?? 90000)}
              />
            )}
          </Field>
          <Field label="Position" hint="Lower numbers come first.">
            {(control) => (
              <Input
                {...control}
                name="position"
                type="number"
                inputMode="numeric"
                min={0}
                step={1}
                required
                defaultValue={existing?.position ?? 0}
              />
            )}
          </Field>
        </div>

        <ul className="divide-y divide-ui-border-base rounded-lg shadow-borders-base">
          <li className="flex items-center justify-between gap-x-4 px-4 py-3">
            <label htmlFor="pack-active" className="min-w-0 cursor-pointer">
              <span className="txt-compact-small-plus block text-ui-fg-base">On sale</span>
              <span className="txt-compact-xsmall block text-ui-fg-muted">
                An inactive pack is hidden from Members. Credits already bought are unaffected.
              </span>
            </label>
            <Switch id="pack-active" checked={active} onCheckedChange={setActive} />
          </li>
        </ul>
      </Drawer.Body>

      <Drawer.Footer>
        {existing ? (
          <Button
            type="button"
            variant="danger"
            size="small"
            className="mr-auto"
            onClick={() => setDeleting(true)}
          >
            Delete
          </Button>
        ) : null}
        <Button type="button" variant="secondary" size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" size="small" isLoading={save.isPending}>
          {existing ? "Save" : "Create credit pack"}
        </Button>
      </Drawer.Footer>

      <Prompt open={deleting} onOpenChange={setDeleting}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Delete {existing?.name}?</Prompt.Title>
            <Prompt.Description>
              It disappears from billing settings. Credits already bought stay with their
              workspaces. To pause sales instead, switch it off.
            </Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Keep it</Prompt.Cancel>
            <Button
              size="small"
              variant="danger"
              isLoading={remove.isPending}
              onClick={() => remove.mutate()}
            >
              Delete
            </Button>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </form>
  );
}
